import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import CalorieCard from '../components/food/CalorieCard';
import FoodLogger from '../components/food/FoodLogger';
import AddProductModal from '../components/food/AddProductModal';
import FoodHistoryModal from '../components/food/FoodHistoryModal';
import foodTrackingService, { DailyNutrition, FoodEntry } from '../services/foodTrackingService';
import { useAuth } from '../contexts/AuthContext';

export default function FoodDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Helper to get local date string (YYYY-MM-DD) instead of UTC
  const getLocalDateString = (d = new Date()) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [dailyData, setDailyData] = useState<DailyNutrition | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLogger, setShowLogger] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>('breakfast');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Edit entry state
  const [editEntry, setEditEntry] = useState<FoodEntry | null>(null);
  const [editForm, setEditForm] = useState({
    foodName: '',
    grams: 100,
    calories_per100: 0,
    protein_per100: 0,
    carbs_per100: 0,
    fat_per100: 0,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadDailyData();
  }, [selectedDate]);

  // Redirect to calorie goals setup if no goals set (mandatory)
  // Check both 'goals' and 'goal' fields for robustness (backend may return either)
  useEffect(() => {
    if (!loading && dailyData && !dailyData.goals && !(dailyData as any).goal) {
      // Double-check by fetching goals directly before redirecting
      foodTrackingService.getGoals().then(() => {
        // Goals exist, just not in daily data response - reload
        loadDailyData();
      }).catch(() => {
        // Goals truly don't exist - redirect to setup
        navigate('/calorie-goals-setup', { state: { from: 'food-tracking' } });
      });
    }
  }, [dailyData, loading, navigate]);

  const loadDailyData = async () => {
    setLoading(true);
    try {
      const data = await foodTrackingService.getDailyNutrition(selectedDate);
      setDailyData(data);
    } catch (error: any) {
      console.error('Error loading daily data:', error);
      // If no goals set, set default data
      if (error.message?.includes('Goals not set')) {
        setDailyData({
          date: selectedDate,
          entries: [],
          totals: { calories: 0, protein: 0, carbs: 0, fat: 0, quantity: 0 },
          remaining: { calories: 2000, protein: 150, carbs: 250, fat: 65 },
          byMealType: {
            breakfast: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            lunch: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            dinner: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            snack: { calories: 0, protein: 0, carbs: 0, fat: 0 },
          },
        });
      } else {
        // On other errors (network, etc.), set default data so page doesn't stay loading
        setDailyData({
          date: selectedDate,
          entries: [],
          totals: { calories: 0, protein: 0, carbs: 0, fat: 0, quantity: 0 },
          remaining: { calories: 2000, protein: 150, carbs: 250, fat: 65 },
          goals: undefined,
          byMealType: {
            breakfast: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            lunch: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            dinner: { calories: 0, protein: 0, carbs: 0, fat: 0 },
            snack: { calories: 0, protein: 0, carbs: 0, fat: 0 },
          },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddMeal = (mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack') => {
    setSelectedMealType(mealType);
    setShowLogger(true);
  };

  const handleDeleteEntry = async (entryId: string) => {
    setDeleting(true);
    try {
      await foodTrackingService.deleteEntry(entryId);
      setDeleteConfirm(null);
      await loadDailyData();
    } catch (error: any) {
      console.error('Delete error:', error);
      alert(error.message || 'Eroare la ștergerea înregistrării');
    } finally {
      setDeleting(false);
    }
  };

  const handleStartEdit = (entry: FoodEntry) => {
    // Calculate per-100g values from the stored entry
    const currentGrams = entry.servingSize || 100;
    const factor = currentGrams > 0 ? 100 / currentGrams : 1;
    setEditForm({
      foodName: entry.foodName,
      grams: currentGrams,
      calories_per100: Math.round(entry.nutrition.calories * factor),
      protein_per100: Math.round(entry.nutrition.protein * factor * 10) / 10,
      carbs_per100: Math.round(entry.nutrition.carbs * factor * 10) / 10,
      fat_per100: Math.round(entry.nutrition.fat * factor * 10) / 10,
    });
    setEditEntry(entry);
  };

  const getEditNutrition = () => {
    const factor = editForm.grams / 100;
    return {
      calories: Math.round(editForm.calories_per100 * factor),
      protein: Math.round(editForm.protein_per100 * factor * 10) / 10,
      carbs: Math.round(editForm.carbs_per100 * factor * 10) / 10,
      fat: Math.round(editForm.fat_per100 * factor * 10) / 10,
    };
  };

  const handleSaveEdit = async () => {
    if (!editEntry) return;
    setSaving(true);
    try {
      const nutrition = getEditNutrition();
      await foodTrackingService.updateEntry(editEntry.id, {
        foodName: editForm.foodName,
        quantity: editForm.grams,
        calories: nutrition.calories,
        protein: nutrition.protein,
        carbs: nutrition.carbs,
        fats: nutrition.fat,
      } as any);
      setEditEntry(null);
      await loadDailyData();
    } catch (error: any) {
      console.error('Edit error:', error);
      alert(error.message || 'Eroare la editarea înregistrării');
    } finally {
      setSaving(false);
    }
  };

  const getMealIcon = (mealType: string) => {
    switch (mealType) {
      case 'breakfast':
        return (
          <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'lunch':
        return (
          <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'dinner':
        return (
          <svg className="w-6 h-6 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        );
      case 'snack':
        return (
          <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        );
    }
  };

  const getMealLabel = (mealType: string) => {
    switch (mealType) {
      case 'breakfast': return 'Mic Dejun';
      case 'lunch': return 'Prânz';
      case 'dinner': return 'Cină';
      case 'snack': return 'Gustare';
      default: return mealType;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Mese & Calorii cu AI"
          subtitle="Fotografiază mâncarea sau adaugă mesele manual, iar AI îți recunoaște mâncarea și îți estimează caloriile și valorile nutriționale."
          showBack
          backTo="/dashboard"
        />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader
        title="Mese & Calorii cu AI"
        subtitle="Fotografiază mâncarea sau adaugă mesele manual, iar AI îți recunoaște mâncarea și îți estimează caloriile și valorile nutriționale."
        showBack
        backTo="/dashboard"
      />

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Date Selector */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                const date = new Date(selectedDate + 'T12:00:00');
                date.setDate(date.getDate() - 1);
                setSelectedDate(getLocalDateString(date));
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="text-center">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={getLocalDateString()}
                className="text-lg font-semibold text-gray-900 border-0 focus:ring-0 text-center cursor-pointer"
              />
              <p className="text-sm text-gray-600">
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('ro-RO', { weekday: 'long' })}
              </p>
            </div>

            <button
              onClick={() => {
                const date = new Date(selectedDate + 'T12:00:00');
                const todayStr = getLocalDateString();
                if (selectedDate < todayStr) {
                  date.setDate(date.getDate() + 1);
                  setSelectedDate(getLocalDateString(date));
                }
              }}
              disabled={selectedDate >= getLocalDateString()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Calorie Target Info Banner - Based on healthGoal */}
        {user?.healthGoal && (() => {
          const calorieTargets: Record<string, { min: number; max: number; label: string; tip: string }> = {
            // HEALTHY MODE
            WEIGHT_LOSS: { min: 1200, max: 1500, label: 'Deficit pentru Slăbit', tip: 'Deficit caloric 300-500 kcal/zi pentru pierdere sănătoasă 0.5kg/săptămână' },
            MUSCLE_GAIN: { min: 2500, max: 3000, label: 'Surplus pentru Masă Musculară', tip: 'Surplus +300-500 kcal/zi + proteine 1.6-2g/kg pentru hipertrofie' },
            HEALTHY_LIFESTYLE: { min: 1900, max: 2100, label: 'Menținere echilibrată', tip: 'Consum = Ardere. Echilibru: 50% carbo, 30% proteine, 20% grăsimi' },
            REDUCE_BELLY_FAT: { min: 1500, max: 1800, label: 'Burn Grăsime Abdominală', tip: 'Deficit moderat + low-carb <100g/zi, focus pe proteine & fibre' },
            INCREASE_ENERGY: { min: 2000, max: 2200, label: 'Energie Optimă', tip: 'Calorii suficiente + index glicemic scăzut, mese la 3-4 ore' },
            CLEAN_EATING: { min: 1800, max: 2000, label: 'Alimentație Curată', tip: 'Focus pe calitate (alimente întregi, max 5 ingrediente), nu cantitate' },
            REDUCE_SUGAR_CARBS: { min: 1600, max: 1900, label: 'Low Carb', tip: 'Carbo <100g/zi, înlocuiește cu proteine & grăsimi sănătoase' },
            BETTER_SLEEP: { min: 1900, max: 2100, label: 'Somn & Recuperare', tip: 'Calorii echilibrate, evită mese grele seara, magneziu & triptofan' },
            MAINTAIN_WEIGHT: { min: 1900, max: 2100, label: 'Menținere Greutate', tip: 'Consum = Ardere, consistență zilnică, flexibilitate 80/20' },
            
            // CHRONIC MODE
            EASY_STANDING: { min: 1800, max: 2000, label: 'Forță & Recuperare', tip: 'Proteine calitate + calciu & vitamina D pentru mușchi & oase' },
            REDUCE_INFLAMMATION: { min: 1700, max: 1900, label: 'Antiinflamator', tip: 'Focus pe omega-3, curcuma, evită zahăr & procesate' },
            RECOVER_MUSCLE_STRENGTH: { min: 1900, max: 2200, label: 'Recuperare Musculară', tip: 'Proteine 1.2-1.5g/kg + leucină, BCAA, antioxidanți' },
            STABILIZE_GLUCOSE: { min: 1700, max: 2000, label: 'Control Glicemie', tip: 'Index glicemic scăzut, fibre la fiecare masă, porții mici frecvente' },
            REDUCE_JOINT_PAIN: { min: 1800, max: 2000, label: 'Joint Health', tip: 'Pește gras 3x/săpt (omega-3), colagen, antioxidanți, hidratare' },
            CONTROL_CHRONIC_PAIN: { min: 1800, max: 2000, label: 'Pain Management', tip: 'Dietă antiinflamatoare, magneziu pentru mușchi, evită trigger foods' },
            HEALTHY_WEIGHT_LOSS_CHRONIC: { min: 1500, max: 1700, label: 'Slăbit Sănătos (Adaptat)', tip: 'Deficit FOARTE mic 0.25-0.5kg/săpt, toate nutrientele esențiale' }
          };

          const target = calorieTargets[user.healthGoal];
          if (!target) return null;

          return (
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 text-white shadow-lg">
              <div className="flex items-start gap-3">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Target className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1">{target.label}</h3>
                  <p className="text-green-50 text-sm mb-3">{target.tip}</p>
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
                      <div className="text-2xl font-bold">{target.min}-{target.max}</div>
                      <div className="text-xs text-green-100">kcal/zi recomandat</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Calorie Card */}
        {dailyData && dailyData.totals && (
          <CalorieCard
            consumed={dailyData.totals.calories || 0}
            goal={dailyData.goals?.targetCalories || 2000}
            remaining={dailyData.remaining?.calories || 2000}
            protein={{
              consumed: dailyData.totals.protein || 0,
              goal: dailyData.goals?.targetProtein || 150,
            }}
            carbs={{
              consumed: dailyData.totals.carbs || 0,
              goal: dailyData.goals?.targetCarbs || 250,
            }}
            fat={{
              consumed: dailyData.totals.fat || 0,
              goal: dailyData.goals?.targetFats || 65,
            }}
          />
        )}

        {/* Quick Add Meal Buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(['breakfast', 'lunch', 'dinner', 'snack'] as const).map((mealType) => (
            <div
              key={mealType}
              className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col"
            >
              <div className="flex items-center justify-center mb-2">{getMealIcon(mealType)}</div>
              <div className="text-sm font-semibold text-gray-900 text-center mb-1">{getMealLabel(mealType)}</div>
              <div className="text-lg font-bold text-primary-600 text-center mb-3">
                {dailyData?.byMealType?.[mealType]?.calories || 0} kcal
              </div>
              <button
                onClick={() => handleAddMeal(mealType)}
                className="w-full text-primary-600 py-1.5 rounded hover:bg-primary-50 transition-colors text-xs font-medium border border-primary-200"
              >
                + Adaugă
              </button>
            </div>
          ))}
        </div>

        {/* Meals by Type */}
        {dailyData && dailyData.entries && dailyData.entries.length > 0 ? (
          <div className="space-y-6">
            {(['breakfast', 'lunch', 'dinner', 'snack'] as const).map((mealType) => {
              const mealEntries = dailyData.entries.filter(e => e.mealType === mealType);
              if (mealEntries.length === 0) return null;

              return (
                <div key={mealType} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getMealIcon(mealType)}
                        <h3 className="font-semibold text-gray-900">{getMealLabel(mealType)}</h3>
                      </div>
                      <div className="text-sm font-semibold text-primary-600">
                        {dailyData.byMealType?.[mealType]?.calories || 0} kcal
                      </div>
                    </div>
                  </div>

                  <div className="p-3 space-y-3">
                    {mealEntries.map((entry) => (
                      <div key={entry.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-all border border-gray-200">
                        <div className="flex items-start gap-4">
                          {/* Food Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-gray-900 truncate">{entry.foodName}</h4>
                              {entry.consumedAt && (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  {new Date(entry.consumedAt).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                            {entry.brand && (
                              <p className="text-sm text-gray-600 truncate">{entry.brand}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs bg-white px-2 py-1 rounded-md text-gray-700 font-medium">
                                {entry.quantity > 1
                                  ? `${entry.quantity} × ${entry.servingSize}${entry.servingUnit}`
                                  : `${entry.servingSize}${entry.servingUnit}`}
                              </span>
                            </div>
                            {entry.notes && (
                              <p className="text-xs text-gray-600 mt-2 italic">{entry.notes}</p>
                            )}
                          </div>

                          {/* Nutrition & Actions */}
                          <div className="flex flex-col items-end gap-2">
                            <div className="text-center bg-primary-50 px-3 py-2 rounded-lg">
                              <div className="text-2xl font-bold text-primary-600">
                                {entry.nutrition.calories}
                              </div>
                              <div className="text-xs text-gray-600">kcal</div>
                            </div>
                            
                            <div className="flex gap-1.5">
                              <div className="bg-blue-50 px-2 py-1 rounded text-xs font-medium text-blue-700">
                                P {entry.nutrition.protein}g
                              </div>
                              <div className="bg-yellow-50 px-2 py-1 rounded text-xs font-medium text-yellow-700">
                                C {entry.nutrition.carbs}g
                              </div>
                              <div className="bg-orange-50 px-2 py-1 rounded text-xs font-medium text-orange-700">
                                F {entry.nutrition.fat}g
                              </div>
                            </div>
                            
                            <div className="flex gap-1.5">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartEdit(entry);
                                }}
                                className="flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-primary-50 transition-colors border border-primary-200"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Editează
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirm(entry.id);
                                }}
                                className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors border border-red-200"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Șterge
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center border border-gray-100">
            <div className="text-6xl mb-4">🍽️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nicio masă înregistrată astăzi
            </h3>
            <p className="text-gray-600 mb-6">
              Începe să-ți urmărești alimentația adăugând prima masă
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setShowLogger(true)}
                className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                Adaugă Prima Masă
              </button>
              <button
                onClick={() => { setSelectedMealType('breakfast'); setShowHistory(true); }}
                className="flex items-center justify-center gap-2 border-2 border-purple-200 text-purple-700 px-6 py-3 rounded-lg hover:bg-purple-50 transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Din Istoric
              </button>
            </div>
          </div>
        )}

        {/* Settings Button */}
        {!dailyData?.goals && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h4 className="font-medium text-yellow-900">Setează-ți obiectivele calorice</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Pentru a urmări mai bine progresul, configurează-ți obiectivele personalizate
                </p>
                <button
                  onClick={() => navigate('/settings')}
                  className="mt-3 text-sm font-medium text-yellow-600 hover:text-yellow-700"
                >
                  Setează Acum →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Food Logger Modal */}
      {showLogger && (
        <FoodLogger
          date={selectedDate}
          mealType={selectedMealType}
          onClose={() => setShowLogger(false)}
          onSuccess={() => {
            loadDailyData();
            setShowLogger(false);
          }}
        />
      )}

      {/* Add Product Modal */}
      {showAddProduct && (
        <AddProductModal
          onClose={() => setShowAddProduct(false)}
          onSuccess={(productId) => {
            console.log('Product added:', productId);
            setShowAddProduct(false);
            alert('Produs adăugat cu succes! Poți să-l cauți acum în lista de produse.');
          }}
        />
      )}

      {/* Food History Modal */}
      {showHistory && (
        <FoodHistoryModal
          date={selectedDate}
          mealType={selectedMealType}
          onClose={() => setShowHistory(false)}
          onSuccess={() => {
            loadDailyData();
            setShowHistory(false);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center sm:p-4 overflow-hidden">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-sm w-full p-6 shadow-2xl" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Șterge înregistrarea?</h3>
              <p className="text-gray-600 text-sm mb-6">
                Sigur vrei să ștergi această masă? Acțiunea nu poate fi anulată.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Anulează
                </button>
                <button
                  onClick={() => handleDeleteEntry(deleteConfirm)}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Se șterge...' : 'Șterge'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Entry Modal */}
      {editEntry && (() => {
        const editNutrition = getEditNutrition();
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center sm:p-4 overflow-hidden">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden shadow-2xl" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-lg font-bold text-gray-900">Editează Aliment</h2>
                <button
                  onClick={() => setEditEntry(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-5">
                {/* Food Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Nume Aliment</label>
                  <input
                    type="text"
                    value={editForm.foodName}
                    onChange={(e) => setEditForm({ ...editForm, foodName: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-medium"
                  />
                </div>

                {/* Grams control */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Gramaj</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setEditForm({ ...editForm, grams: Math.max(0, editForm.grams - 10) })}
                      className="w-10 h-10 rounded-full bg-red-100 text-red-600 font-bold text-xl flex items-center justify-center hover:bg-red-200 transition-colors flex-shrink-0"
                    >
                      −
                    </button>
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={editForm.grams === 0 ? '' : editForm.grams}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, '');
                          setEditForm({ ...editForm, grams: raw === '' ? 0 : parseInt(raw, 10) });
                        }}
                        onBlur={(e) => {
                          if (!e.target.value || editForm.grams <= 0) {
                            setEditForm({ ...editForm, grams: 0 });
                          }
                        }}
                        placeholder="0"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-center font-bold text-xl"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">g</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditForm({ ...editForm, grams: editForm.grams + 10 })}
                      className="w-10 h-10 rounded-full bg-green-100 text-green-600 font-bold text-xl flex items-center justify-center hover:bg-green-200 transition-colors flex-shrink-0"
                    >
                      +
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 text-center mt-1">± 10g per apăsare</p>
                </div>

                {/* Calculated nutrition preview */}
                <div className="bg-gradient-to-r from-primary-50 to-blue-50 border-2 border-primary-200 rounded-xl p-4">
                  <p className="text-sm font-bold text-gray-700 mb-3 text-center">
                    Total pentru {editForm.grams}g
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="text-center bg-white rounded-lg p-2 shadow-sm">
                      <div className="text-xl font-bold text-orange-600">{editNutrition.calories}</div>
                      <div className="text-xs text-gray-500">kcal</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-2 shadow-sm">
                      <div className="text-lg font-bold text-blue-600">{editNutrition.protein}g</div>
                      <div className="text-xs text-gray-500">Prot</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-2 shadow-sm">
                      <div className="text-lg font-bold text-yellow-600">{editNutrition.carbs}g</div>
                      <div className="text-xs text-gray-500">Carbo</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-2 shadow-sm">
                      <div className="text-lg font-bold text-red-600">{editNutrition.fat}g</div>
                      <div className="text-xs text-gray-500">Grăsimi</div>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setEditEntry(null)}
                    disabled={saving}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Anulează
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={saving || editForm.grams <= 0}
                    className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Salvez...' : '✓ Salvează'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
